import React, { useCallback, useMemo } from "react";
import { styles } from "./form";
import { useFormik } from "formik";
import { EnSchema } from "./en_schema";
import { ArSchema } from "./ar_schema";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import { api } from "@/utils/Axios";
import { useI18n } from "@/i18n/I18nProvider";


function Form({ color }) {
 const { t, locale } = useI18n();

  const validationSchema = useMemo(
    () => (locale === "en" ? EnSchema : ArSchema),
    [locale]
  );

  const initialValues = useMemo(
    () => ({
      email: "",
      phoneNumber: "",
      subject: "",
      message: "",
      username: "",
    }),
    []
  );

  const onSubmit = useCallback(
    async (values, actions) => {
      try {
        await api.post(`/general/contact-form`, {
          email: values.email,
          fullName: values.username,
          mobileNumber: `+2${String(values.phoneNumber || "")}`,
          subject: values.subject,
          message: values.message,
        });

        actions.resetForm();

        // Lazy-load toast (أفضل لـ bundle + INP)
        const { toast } = await import("react-toastify");
        toast.success(t.formMsg, {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } catch (error) {
        const { toast } = await import("react-toastify");
        toast.error(t.formMsgFail, {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      }
    },
    [t]
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    // تحسين INP: متعملش validation على كل حرف
    validateOnChange: false,
    validateOnBlur: true,
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
  } = formik;

  const handlePhoneChange = useCallback(
    (event) => {
      const raw = event.target.value || "";
      const digitsOnly = raw.replace(/\D/g, "").slice(0, 11);
      if (digitsOnly !== raw) event.target.value = digitsOnly;
      handleChange(event);
    },
    [handleChange]
  );

  return (
    <Box
      component="form"
      sx={styles.container}
      onSubmit={handleSubmit}
      autoComplete="off"
      noValidate
    >
      <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
        <InputLabel htmlFor="username" sx={styles.label}>
          {t.form_labels.full_name}
        </InputLabel>
        <TextField
          id="username"
          name="username"
          value={values.username}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          autoComplete="name"
          error={Boolean(touched.username && errors.username)}
          sx={styles.input}
          InputProps={{ sx: { height: "45px" } }}
          variant="outlined"
        />
        <Box sx={styles.helperText}>{touched.username && errors.username}</Box>
      </Box>

      <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
        <InputLabel htmlFor="email" sx={styles.label}>
          {t.form_labels.email_adress}
        </InputLabel>
        <TextField
          id="email"
          name="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          type="email"
          autoComplete="email"
          error={Boolean(touched.email && errors.email)}
          sx={styles.input}
          InputProps={{ sx: { height: "45px" } }}
          variant="outlined"
        />
        <Box sx={styles.helperText}>{touched.email && errors.email}</Box>
      </Box>

      <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
        <InputLabel htmlFor="phoneNumber" sx={styles.label}>
          {t.form_labels.phone_number}
        </InputLabel>
        <TextField
          id="phoneNumber"
          name="phoneNumber"
          value={values.phoneNumber}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
          type="text"
          inputMode="numeric"
          autoComplete="tel"
          error={Boolean(touched.phoneNumber && errors.phoneNumber)}
          sx={styles.input}
          InputProps={{ sx: { height: "45px" } }}
          variant="outlined"
        />
        <Box sx={styles.helperText}>
          {touched.phoneNumber && errors.phoneNumber}
        </Box>
      </Box>

      <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
        <InputLabel htmlFor="subject" sx={styles.label}>
          {t.form_labels.subject}
        </InputLabel>
        <TextField
          id="subject"
          name="subject"
          value={values.subject}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          autoComplete="off"
          error={Boolean(touched.subject && errors.subject)}
          sx={styles.input}
          InputProps={{ sx: { height: "45px" } }}
          variant="outlined"
        />
        <Box sx={styles.helperText}>{touched.subject && errors.subject}</Box>
      </Box>

      <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
        <InputLabel htmlFor="message" sx={styles.label}>
          {t.form_labels.message}
        </InputLabel>
        <TextField
          id="message"
          name="message"
          multiline
          rows={3}
          value={values.message}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          autoComplete="off"
          error={Boolean(touched.message && errors.message)}
          sx={styles.input}
          variant="outlined"
        />
        <Box sx={styles.helperText}>{touched.message && errors.message}</Box>
      </Box>

      <Button
        aria-label="contact us submit button"
        disabled={isSubmitting}
        type="submit"
        sx={styles.btn}
        style={{
          backgroundColor: color,
          border: `1px solid #74357D`,
        }}
      >
        {t.form_labels.submit}
      </Button>
    </Box>
  );
}

export default React.memo(Form);
